import React, { Component } from 'react';
import cx from 'classnames/bind';
import { Link, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import Application from '../src/components/Application/Application';
import ApplicationHeader from '../src/components/ApplicationHeader/ApplicationHeader';
import Button from '../src/components/Button/Button';
import Icon from '../src/components/Icon/Icon';
import List from '../src/components/List/List';
import Sidebar from '../src/components/Sidebar/Sidebar';
import SidebarTab from '../src/components/SidebarTab/SidebarTab';
import Colors from './pages/Colors';
import Typography from './pages/Typography';
import Welcome from './pages/Welcome';
import Example from './components/Example';
import packageJson from '../package.json';

// get all component names in src/components
const comps = require.context('../src/components', true, /^(?!.*(Test)).*\.jsx$/);
const components = comps
  .keys()
  .filter(key => {
    const split = key.split('/');
    return key.indexOf('Example.jsx') === -1 && `${split[split.length - 2]}.jsx` === split[split.length - 1];
  })
  .map(key => key.match(/.*\/(.*).jsx$/)[1]);

// require all *Example.jsx files under src/components
const examples = require.context('../src/components', true, /.*Example\.jsx$/);
const exampleNames = examples.keys();
const examplePanels = exampleNames.map(examples);

function withRouter(Child) {
  return function WrappedComponent(props) {
    const navigate = useNavigate();
    return <Child {...props} navigate={navigate} />;
  };
}

/**
 * This app lists all components in *vimond-core-components*.
 */
class Examples extends Component {

  constructor(props) {
    super(props);
    this.routes = [{
      title: 'Welcome',
      path: '/welcome',
      tags: 'introduction',
      page: () => <Welcome/>,
    }, {
      title: 'Colors',
      path: '/colors',
      tags: '',
      page: () => <Colors/>,
    }, {
      title: 'Typography',
      path: '/typography',
      tags: 'font roboto',
      page: () => <Typography/>,
    }].concat(components.map(name => {
      const file = `./${name}/${name}Example.jsx`;
      const pos = exampleNames.indexOf(file);
      let example;

      if (pos > -1 && examplePanels[pos]) {
        example = React.createElement(examplePanels[pos].default);
      }
      return {
        title: name,
        path: `/${name.toLowerCase()}`,
        tags: (example && example.type && example.type.tags) || '',
        page: () => (example ? React.cloneElement(example, { title: name }) : <Example title={name}>TODO: Add example</Example>),
      };
    }));
    this.state = { items: this.routes };
    window.showAppInfo = (info, op) => this.setState({ info: `${op || 'Clicked'} <strong>${info}</strong>` });
  }

  componentDidMount() {
    this.scrollToSelectionIfNecessary();
    window.addEventListener('click', this.onCloseProperties);
  }

  componentDidUpdate() {
    if(this.url !== window.location.pathname){
      this.scrollToSelectionIfNecessary();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.onCloseProperties);
  }

  onClickPageMenu = e => {
    e.preventDefault();
    this.setState({ showPageMenu: !this.state.showPageMenu });
  };

  onCloseProperties = e => {
    if (this.state.forceProps) {
      const properties = document.querySelector('.properties');

      if (properties && properties.contains && !properties.contains(e.target)) {
        this.setState({ forceProps: false });
      }
    }
  };

  onClosePageMenu = () => this.setState({ showPageMenu: false });

  onClickSidebar = () => this.state.showPageMenu && this.setState({ showPageMenu: false });

  onSearch = e => {
    const value = (e.target && e.target.value.toLowerCase()) || '';
    let items = this.routes;

    if (value.length > 0) {
      items = this.routes.filter(item => item.title.toLowerCase().indexOf(value) > -1 || item.tags.indexOf(value) > -1);
    }
    this.setState({ items });
  };

  onToggleFullscreen = () => this.setState({ fullscreen: !this.state.fullscreen });

  onToggleProps = e => {
    e.stopPropagation();
    this.setState({ forceProps: !this.state.forceProps });
  };

  itemRenderer = route => (
    <Link className={route.path === window.location.pathname ? 'selected' : undefined} to={route.path} onClick={this.onClickSidebar}>
      {route.title}
    </Link>);

  scrollToSelectionIfNecessary = () => {
    if (this.url !== window.location.pathname) {
      setTimeout(() => {
        const list = document.querySelectorAll('.sidebar a.selected');
        const pages = document.getElementsByClassName('pages');
        const page = document.getElementsByClassName('page');

        this.url = window.location.pathname;
        if (list && list.length && list[0].scrollIntoViewIfNeeded) {
          list[0].scrollIntoViewIfNeeded(true);
        }
        if (pages && pages.length) {
          pages[0].scrollTop = 0;
        }
        this.setState({ noProps: page && page[0] && page[0].classList.contains('no-props') });
      }, 50);
    }
  };

  render() {
    const { forceProps, fullscreen, items, noProps } = this.state;
    let { info } = this.state;

    info = info ? <div className="app-info" dangerouslySetInnerHTML={{ __html: info }}/> : undefined; // eslint-disable-line react/no-danger
    return (
      <Application className={cx({ fullscreen, 'page-menu': this.state.showPageMenu, 'force-props': forceProps, 'no-props': noProps })}>
        <ApplicationHeader title="vimond-core-components" version={packageJson.version} onClick={this.onClickPageMenu}>
          <div className="close-page-menu"><Icon name="close" onClick={this.onClosePageMenu}/></div>
          {info}
          <Button className="core-icon-button toggle-fullscreen" iconName={fullscreen ? 'exit-fullscreen' : 'fullscreen'}
            onClick={this.onToggleFullscreen} title="Toggle maximize"/>
          <Button className="core-icon-button toggle-props" iconName={forceProps ? 'close' : 'settings'} onClick={this.onToggleProps}/>
        </ApplicationHeader>
        <div className="application-content">
          <Sidebar className="sidebar">
            <SidebarTab onChange={this.onSearch} placeholder="Type to filter components">
              <List items={items} itemRenderer={this.itemRenderer} data-key={location.pathname}/>
            </SidebarTab>
          </Sidebar>
          <div className="pages">
            <Routes>
              <Route exact path="/" element={<Navigate to="/welcome" replace />} />
              {this.routes.map(route => <Route key={route.title} path={route.path} Component={route.page}/>)}
            </Routes>
          </div>
        </div>
      </Application>
    );
  }

}

export default withRouter(Examples);
